import React from 'react'
import { OmSelect } from '@owma/kit'

import i18n from 'i18n'
import classNames from 'classnames'
import dayjs from 'dayjs'

import { confirmBase } from 'utils/ui'
import { SVGImageCurrentColor } from 'utils/svgHelper'
import toolbarDeleteSVG from 'assets/images/toolbar-delete.svg'
import chevronDown24 from 'assets/images/toolbar-chevron-down.svg'

import styles from './style.module.scss'

type SecurityMode = 'normal' | 'confidential'

export type ExpirationKey = '1day' | '1week' | '1month' | '6months' | '1year'

interface SecureSendOptionsParams {
  mode: SecurityMode
  expirationKey: string | null
  onSave: (mode: SecurityMode, expired: string | null, expirationKey: string | null) => void
  onTrash: () => void
  renewMode?: boolean
}

const EXPIRATION_OPTIONS: { key: ExpirationKey; labelKey: string; amount: number; unit: string }[] =
  [
    { key: '1day', labelKey: 'secureSend.popup.expiresIn1Day', amount: 1, unit: 'day' },
    { key: '1week', labelKey: 'secureSend.popup.expiresIn1Week', amount: 1, unit: 'week' },
    {
      key: '1month',
      labelKey: 'secureSend.popup.expiresIn1Month',
      amount: 1,
      unit: 'month',
    },
    {
      key: '6months',
      labelKey: 'secureSend.popup.expiresIn6Months',
      amount: 6,
      unit: 'month',
    },
    { key: '1year', labelKey: 'secureSend.popup.expiresIn1Year', amount: 1, unit: 'year' },
  ]

export function calculateExpiredISO(expirationKey: ExpirationKey, baseTime?: dayjs.Dayjs): string {
  const base = baseTime ?? dayjs()
  const option = EXPIRATION_OPTIONS.find((o) => o.key === expirationKey)
  if (option == null) return base.add(1, 'week').toISOString()
  return base.add(option.amount, option.unit as dayjs.ManipulateType).toISOString()
}

// 예약 메일 응답에는 expirationKey 자체가 없고 expires_at(ISO)만 있다.
// scheduled_time 기준으로 각 EXPIRATION_OPTIONS 후보를 만든 뒤
// expires_at 과의 시간 차가 최소인 key 를 반환한다 (캘린더 단위 차이를 정확히 반영).
export function findClosestExpirationKey(
  scheduledTime: dayjs.Dayjs | string,
  expiresAt: string,
): ExpirationKey {
  const base = dayjs(scheduledTime)
  const target = dayjs(expiresAt).valueOf()

  let bestKey: ExpirationKey = EXPIRATION_OPTIONS[0].key
  let bestDiff = Infinity
  for (const option of EXPIRATION_OPTIONS) {
    const candidateMs = dayjs(calculateExpiredISO(option.key, base)).valueOf()
    const diff = Math.abs(candidateMs - target)
    if (diff < bestDiff) {
      bestDiff = diff
      bestKey = option.key
    }
  }
  return bestKey
}

function formatExpiredDate(isoString: string): string {
  const lang = i18n.language || 'en-US'
  return new Date(isoString).toLocaleDateString(lang, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function secureSendOptions(options: SecureSendOptionsParams): void {
  const isRenewMode = options.renewMode === true

  const tmpObj: Record<string, any> = {
    initialMode: options.mode,
    initialExpirationKey: options.expirationKey || '1week',
    currentMode: isRenewMode ? 'confidential' : options.mode,
    currentExpirationKey: options.expirationKey || '1week',
    btnDisabled: isRenewMode ? false : true,
  }

  tmpObj.isChanged = (): boolean => {
    if (tmpObj.currentMode !== tmpObj.initialMode) return true
    if (
      tmpObj.currentMode === 'confidential' &&
      tmpObj.currentExpirationKey !== tmpObj.initialExpirationKey
    )
      return true
    return false
  }

  tmpObj.onModeChange = (mode: SecurityMode): void => {
    tmpObj.currentMode = mode
    tmpObj.btnDisabled = isRenewMode ? false : !tmpObj.isChanged()
    tmpObj.updateModal()
  }

  tmpObj.onExpirationChange = (key: ExpirationKey): void => {
    tmpObj.currentExpirationKey = key
    tmpObj.btnDisabled = isRenewMode ? false : !tmpObj.isChanged()
    tmpObj.updateModal()
  }

  tmpObj.render = (): React.ReactElement => {
    const isConfidential = tmpObj.currentMode === 'confidential'
    const expiredISO = calculateExpiredISO(tmpObj.currentExpirationKey)

    return (
      <div className={classNames(styles.body)}>
        {!isRenewMode && (
          <OmSelect
            popupClassName={styles.selectMenu}
            className={classNames(styles.modeSelect)}
            suffixIcon={<img src={chevronDown24} alt="chevron-down" />}
            value={tmpObj.currentMode}
            onChange={tmpObj.onModeChange}
            options={[
              { value: 'normal', label: i18n.t('secureSend.popup.optionNormal') },
              {
                value: 'confidential',
                label: i18n.t('secureSend.popup.optionConfidential'),
              },
            ]}
          />
        )}
        {isConfidential && (
          <>
            <div className={classNames(styles.confidentialDesc)}>
              {i18n.t('secureSend.popup.confidentialDesc')}
            </div>
            <div className={classNames(styles.expirationRow)}>
              <span className={classNames(styles.expirationLabel)}>
                {i18n.t('secureSend.popup.expirationLabel')}
              </span>
              <div className={classNames(styles.expirationForm)}>
                <OmSelect
                  popupClassName={styles.selectMenu}
                  className={classNames(styles.expirationSelect)}
                  suffixIcon={<img src={chevronDown24} alt="chevron-down" />}
                  value={tmpObj.currentExpirationKey}
                  onChange={tmpObj.onExpirationChange}
                  options={EXPIRATION_OPTIONS.map((opt) => ({
                    value: opt.key,
                    /* i18next-extract-disable-next-line */
                    label: i18n.t(opt.labelKey),
                  }))}
                />
                <span className={classNames(styles.expirationSummary)}>
                  {formatExpiredDate(expiredISO)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  tmpObj.onTrash = (): void => {
    options.onTrash()
    tmpObj.modalObj.destroy()
  }

  tmpObj.modalObj = confirmBase({
    wrapClassName: classNames(styles.secureSendPopup),
    className: classNames('mobile-fullpage'),
    title: i18n.t('secureSend.popup.title'),
    content: tmpObj.render(),
    okText: i18n.t('secureSend.popup.save'),
    cancelText: i18n.t('secureSend.popup.cancel'),
    width: 480,
    maskClosable: false,
    okButtonProps: {
      className: classNames('type1', 'h38', 'w100'),
      disabled: tmpObj.btnDisabled,
    },
    closeButtonProps: {
      className: classNames('type5', 'h38', 'w100'),
    },
    footer: (_: unknown, { OkBtn, CancelBtn }: any): React.ReactElement => (
      <div className={classNames(styles.footer)}>
        {!isRenewMode && tmpObj.currentMode === 'confidential' && (
          <button type="button" className={classNames(styles.trashBtn)} onClick={tmpObj.onTrash}>
            <SVGImageCurrentColor src={toolbarDeleteSVG} alt="delete" />
          </button>
        )}
        <div className={classNames(styles.footerSpacer)} />
        <CancelBtn />
        <OkBtn />
      </div>
    ),
    onOk: (closeFunc: () => void): void => {
      const isConfidential = tmpObj.currentMode === 'confidential'
      const expired = isConfidential ? calculateExpiredISO(tmpObj.currentExpirationKey) : null
      const expirationKey = isConfidential ? tmpObj.currentExpirationKey : null
      options.onSave(tmpObj.currentMode, expired, expirationKey)
      closeFunc()
    },
  })

  tmpObj.updateModal = (): void => {
    tmpObj.modalObj.updateBase({
      content: tmpObj.render(),
      okButtonProps: {
        className: classNames('type1', 'h38', 'w100'),
        disabled: tmpObj.btnDisabled,
      },
      footer: (_: unknown, { OkBtn, CancelBtn }: any): React.ReactElement => (
        <div className={classNames(styles.footer)}>
          {tmpObj.currentMode === 'confidential' && (
            <button type="button" className={classNames(styles.trashBtn)} onClick={tmpObj.onTrash}>
              <SVGImageCurrentColor src={toolbarDeleteSVG} alt="delete" />
            </button>
          )}
          <div className={classNames(styles.footerSpacer)} />
          <CancelBtn />
          <OkBtn />
        </div>
      ),
    })
  }
}
